import React, { useState, useRef, useEffect } from 'react';
import { IoChevronBackSharp, IoChevronForwardSharp } from 'react-icons/io5';
import { searchFlights } from '../../utils/flightSearch';

const DateSelector = ({ selectedDate, onDateSelect, fromCode, toCode, searchResults = [] }) => {
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [leftArrowActive, setLeftArrowActive] = useState(false);
  const [rightArrowActive, setRightArrowActive] = useState(false);
  
  // Generate dates dynamically
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate 14 days starting from today
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const dateKey = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const dayNum = date.getDate();
      
      const formattedDay = `${dayName}, ${monthName} ${dayNum}`;
      
      // Calculate lowest price from available flights for this date
      let lowestPrice = null;
      if (searchResults.length > 0) {
        const pricesForDate = [];
        searchResults.forEach(flight => {
          if (flight.pricing && flight.pricing[dateKey]) {
            const pricing = flight.pricing[dateKey];
            if (pricing.mainPrice) {
              const numPrice = parseInt(pricing.mainPrice.replace(/[^0-9]/g, ''));
              pricesForDate.push(numPrice);
            }
          }
        });
        
        if (pricesForDate.length > 0) {
          lowestPrice = Math.min(...pricesForDate);
        }
      }
      
      const isPast = date < today;
      
      dates.push({
        day: formattedDay,
        price: lowestPrice ? `$${lowestPrice.toLocaleString()}` : '--',
        available: !isPast && lowestPrice !== null,
        selected: selectedDate === formattedDay,
        dateKey: dateKey
      });
    }
    
    return dates;
  };
  
  const dates = generateDates();

  const scrollLeft = () => {
    setLeftArrowActive(true);
    setTimeout(() => setLeftArrowActive(false), 150);
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.children[0].offsetWidth;
      scrollRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    setRightArrowActive(true);
    setTimeout(() => setRightArrowActive(false), 150);
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.children[0].offsetWidth;
      scrollRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const handleWheel = (e) => {
    if (scrollRef.current) {
      e.preventDefault();
      const scrollAmount = 158; // Width of one date card (148px + 10px margin)
      
      if (e.deltaY > 0) {
        // Scroll down = move right
        setRightArrowActive(true);
        setTimeout(() => setRightArrowActive(false), 150);
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      } else {
        // Scroll up = move left
        setLeftArrowActive(true);
        setTimeout(() => setLeftArrowActive(false), 150);
        scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        scrollContainer.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  return (
    <div className="relative overflow-hidden" 
         style={{
           height: '75px',
           backgroundColor: '#fff'
         }}>
      <div className="relative h-full">
        <button 
          onClick={scrollLeft}
          className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 ${leftArrowActive ? 'text-[#0078d2]' : ''}`}
          style={{
            width: '20px',
            height: '44px',
            marginLeft: '14px',
            color: '#0078d2',
            fontSize: '28px',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: '0'
          }}
        >
          <IoChevronBackSharp size={20} />
        </button>
        
        <div 
          ref={scrollRef}
          className="flex overflow-x-auto scroll-smooth scrollbar-hide h-full"
          style={{
            marginLeft: '40px',
            marginRight: '40px',
            paddingTop: '15px'
          }}
        >
          {dates.map((date, index) => (
            <button
              key={index}
              className={`flex-shrink-0 flex flex-col justify-center items-center transition-all duration-200 ${
                date.available ? 'cursor-pointer' : 'cursor-default'
              } ${date.selected ? 'selected-slide' : ''}`}
              style={{
                width: '148px',
                height: '59px',
                marginRight: '10px',
                marginTop: '-5px',
                backgroundColor: '#fff',
                color: date.available ? '#0078d2' : '#627a88',
                border: 'none',
                textAlign: 'center',
                pointerEvents: date.available ? 'auto' : 'none'
              }}
              onClick={() => date.available && onDateSelect(date.day)}
            >
              <span 
                className="weekly-date text-sm font-normal"
                style={{ 
                  color: date.selected ? '#0078d2' : date.available ? '#0078d2' : '#627a88',
                  marginBottom: '2px'
                }}
              >
                {date.day}
              </span>
              <span 
                className="weekly-price text-lg font-normal"
                style={{ 
                  color: date.selected ? '#0078d2' : date.available ? '#0078d2' : '#627a88'
                }}
              >
                {date.price}
              </span>
            </button>
          ))}
        </div>

        <button 
          onClick={scrollRight}
          className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 border-2 border-[#0078d2] ${rightArrowActive ? 'text-[#0078d2]' : ''}`}
          style={{
            width: '20px',
            height: '44px',
            marginRight: '14px',
            color: '#0078d2',
            fontSize: '28px',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: '0'
          }}
        >         
          <IoChevronForwardSharp size={20} />
        </button>
      </div>
    </div>
  );
};

export default DateSelector;
